import React, {
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import './shoppingCart.less';
import ShoppingCartHeader from './widgets/ShoppingCartHeader';
import ShoppingCartEmpty from './widgets/ShoppingCartEmpty';
import { isEmpty } from 'lodash';
import { AiFillCloseCircle } from 'react-icons/ai';
import DiscountCode from './widgets/DiscountCode';
import ShoppingCartSubsidize from './widgets/ShoppingCartSubsidize';
import { Button, message, Modal, Spin, Tooltip } from 'antd';
import useHandleCheckout from './hooks/useHandleCheckout';
import { MainProductType } from './interfaces';
import { Product, setWholeCart, TransferProduct } from './ShoppingCartSliceV2';
import AgentActions from './agentActions/AgentActions';
import ShoppingCartActivity from './products/ShoppingCartActivity/ShoppingCartActivity';
import ShoppingCartRoom from './products/ShoppingCartRoom/ShoppingCartRoom';
import ShoppingCartPackage from './products/ShoppingCartPackage/ShoppingCartPackage';
import ShoppingCartHotel from './products/ShoppingCartHotel/ShoppingCartHotel';
import ShoppingCartFlight from './products/ShoppingCartFlight/ShoppingCartFlight';
import ShoppingCartFlightPackage from './products/ShoppingCartFlightPackage/ShoppingCartFlightPackage';
import useInitializeCurrentCustomer from './hooks/useInitializeCurrentCustomer';
import { MdWarning } from 'react-icons/md';
import useUpdateCommissionNeeded from './hooks/useIsUpdateCommissionNeeded';
import { useNavigate } from 'react-router-dom';
import CartNotValid from './widgets/CartNotValid';
import AddUserModal from './agentActions/AddUserModal/AddUserModal';
import ShoppingCartOneWayFlight from './products/ShoppingCartOneWayFlight/ShoppingCartOneWayFlight';
import { packageLink } from '../../utils/LinkGenerator';
import useCssVariables from '../../hooks/useCssVariables';
import ButtonWithIcon from '../../components/Common/ButtonWithIcon';
import { FaRegSave } from 'react-icons/fa';
import { updateCommission } from '../Checkout/CheckoutApis';
import useCleanUpLocalStorage from './hooks/useCleanUpLocalStorage';
import ShoppingCartAttraction from './products/ShoppingCartAttraction/ShoppingCartAttraction';
import ShoppingCartTransfer from './products/ShoppingCartTransfer/ShoppingCartTransfer';
import DiamondSVG from '../../assets/svgs/DiamondSVG';
import CashbackDetails from './widgets/cashback/CashbackDetails';


interface Props {
  pageType: string;
  open?: boolean;
  closeFromHeader?: () => void;
  showDiscounCodeOrIntegrationInfo?: boolean;
  isMobileView?: boolean;
  isLocalInventory?: any;
}

const ShoppingCartV2 = ({
  pageType,
  open,
  closeFromHeader,
  showDiscounCodeOrIntegrationInfo = false,
  isMobileView = false,
  isLocalInventory
}: Props) => {
  const isCheckoutPage = pageType === 'checkout' || pageType === 'manage-order';
  const isMangeOrderPage = pageType === 'manage-order';
  const [shoppingCartOpen, setShoppingCartOpen] = useState(false);
  const {
    shoppingCartTranslations: t,
    roomPricing: t_roomPricing,
    wallet: t_wallet,
  } = useSelector((data: RootState) => data.config.translations);

  const { appliedSubsidize } = useSelector(
    (state: RootState) => state.shoppingCart
  );
  const { ShoppingCartV2, agent_commission,  } = useSelector(
    (state: RootState) => state.shoppingCartV2
  );

  const [addUserModal, setAddUserModal] = useState({
    open: false,
    customer_secret: '',
  });
  const isUpdateCommissionNeeded = useUpdateCommissionNeeded();

  useCleanUpLocalStorage();

  const { agentInfo, config } = useSelector((data: RootState) => data.config);

  useEffect(() => {
    if (!!ShoppingCartV2?.orderId && !isEmpty(ShoppingCartV2?.orderId)) {
      setShoppingCartOpen(true);
    }
  }, [ShoppingCartV2]);

  const navigate = useNavigate();
  const checkout = useHandleCheckout(isCheckoutPage);
  const handleCheckout = () => {
    const { orderId, isSaved, orderSecret } = ShoppingCartV2 || {};
    // Handle case when no order exists
    if (!orderId) {
      console.warn('Cannot checkout: No order ID found');
      message.warning('Cannot checkout: No order ID found');
      return;
    }

    // Order exists but not saved or missing secret - proceed to checkout
    if (!isSaved || !orderSecret) {
      checkout();
      return;
    }

    // Order is saved with secret - navigate to order management page
    navigate(`order/manage?id=${orderSecret}`);
  };

  const { settings } = useSelector((state: RootState) => state.shoppingCart);

  const showCloseButton = useMemo(
    () =>
      (open || shoppingCartOpen) &&
      pageType !== 'checkout' &&
      pageType !== 'manage-order',
    [open, pageType, shoppingCartOpen]
  );

  const freePackage = useMemo(() => {
    if (pageType === 'manage-order' && settings?.price_option_type === 'free')
      return true;
    if (settings?.price_option_type === 'free') return true;
    return false;
  }, [settings, pageType]);

  const className = settings?.flight_only ? 'flight-only' : '';

  const memoizedIsUpdateNeeded = useMemo(() => {
    return isUpdateCommissionNeeded();
  }, [isUpdateCommissionNeeded]);

  const generateCssVariables = useCssVariables();

  const dispatch = useDispatch();
  useEffect(() => {
    if (!!ShoppingCartV2?.orderId) {
      dispatch(
        setWholeCart({
          ...ShoppingCartV2,
          isSaved: !isUpdateCommissionNeeded(),
        })
      );
    }
  }, [memoizedIsUpdateNeeded]);

  useInitializeCurrentCustomer();

  const [isUpdateCommissionLoading, setIsUpdateCommissionLoading] =
    useState(false);
  const handleUpdateCommission = async (e: any) => {
    e.stopPropagation();
    await updateCommission(
      {
        ...ShoppingCartV2,
        agent_commission: {
          ...agent_commission,
          is_commission_per_order: 1,
        },
      },
      ShoppingCartV2?.orderSecret ?? '',
      setIsUpdateCommissionLoading
    );
    dispatch(
      setWholeCart({
        ...ShoppingCartV2,
        agent_commission: {
          ...agent_commission,
        },
      })
    );
  };

  const showProductPrice = !(
    ShoppingCartV2?.customerInfo?.agency_id && !agentInfo?.agentMode
  );
  // const showProductPrice = !ShoppingCartV2?.customerInfo?.agency_id || agentInfo?.agentMode;

  // this function is render each product ( * also the sub products)
  const ProductRenderer = useCallback(
    ({
      product,
      identifier,
      isTopLevel = false,
      topLevelProductId,
      isSameDates = false,
      transfer_type,
    }: {
      product: Product;
      identifier?: string;
      isTopLevel?: boolean;
      topLevelProductId?: string | number;
      isSameDates?: boolean;
      transfer_type?: string;
    }) => {
      const topLevelProductData = ShoppingCartV2?.products?.find(
        (prd: Product) =>
          prd.identifier === topLevelProductId || prd.id === topLevelProductId
      );

      let dynamicPackageRoomProduct: Product | undefined = undefined;
      if (
        topLevelProductData?.type === MainProductType.DynamicPackage &&
        product.type === MainProductType.Room
      ) {
        const flight = topLevelProductData?.subProducts?.find(
          (prd: Product) => prd.type === MainProductType.Flights
        );
        const flightTotalPrice = flight?.total_net_price || 0;
        dynamicPackageRoomProduct = {
          ...product,
          roomAndFlightPricePerPax: product.total_net_price + flightTotalPrice,
        };
      }

      switch (product?.type) {
        case MainProductType.Activity:
          return (
            <ShoppingCartActivity
              freePackage={freePackage}
              isCheckoutPage={isCheckoutPage}
              product={product}
              showProductPrice={showProductPrice}
            />
          );
        case MainProductType.Attraction:
          return (
            <ShoppingCartAttraction
              product={product}
              identifier={identifier as string}
              isCheckoutPage={isCheckoutPage}
              showProductPrice={showProductPrice}
            />
          );
        case MainProductType.Flights:
          return (
            <ShoppingCartFlightPackage
              product={product}
              isCheckoutPage={isCheckoutPage}
              showProductPrice={showProductPrice}
              // if Bundle_flight is not a top level product, no need to show "X" close:
              hideCloseIcon={isCheckoutPage || !isTopLevel}
              isTopLevel={isTopLevel}
            />
          );
        case MainProductType.oneWayFlights:
          return (
            <ShoppingCartOneWayFlight
              product={product}
              isCheckoutPage={isCheckoutPage}
              showProductPrice={showProductPrice}
            />
          );
        case MainProductType.Flight:
        case MainProductType.oneWayFlight:
          return (
            <ShoppingCartFlight
              product={product}
              showProductPrice={showProductPrice}
            />
          );
        case MainProductType.Room:
          return (
            <ShoppingCartRoom
              freePackage={freePackage}
              product={dynamicPackageRoomProduct || product}
              isCheckoutPage={isCheckoutPage}
              identifier={identifier}
              isSameDates={isSameDates}
              showProductPrice={showProductPrice}
            />
          );
        case MainProductType.Hotel:
          return (
            <ShoppingCartHotel
              ProductRenderer={(props) =>
                ProductRenderer({
                  ...props,
                  topLevelProductId:
                    topLevelProductId || product?.identifier || product?.id,
                })
              }
              product={product}
              identifier={identifier}
              isTopLevel={isTopLevel}
              showProductPrice={showProductPrice}
            />
          );
        case MainProductType.DynamicPackage:
        case MainProductType.Package:
          return (
            <ShoppingCartPackage
              subProducts={product.subProducts || []}
              product={product}
              ProductRenderer={(props) =>
                ProductRenderer({
                  ...props,
                  topLevelProductId:
                    topLevelProductId || product?.identifier || product?.id,
                })
              }
              identifier={identifier}
              showProductPrice={showProductPrice}
              mainProductType={product?.type}
            />
          );
        case MainProductType.transfer:
          return (
            <ShoppingCartTransfer
              product={product as unknown as TransferProduct}
              transfer_type={transfer_type}
            />
          );
        default:
          return null;
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [freePackage, isCheckoutPage, showProductPrice, ShoppingCartV2?.products]
  );

  const isAgent = !isEmpty(agentInfo);
  const [cashbackDetailsVisible, setCashbackDetailsVisible] = useState(false);
  const showCashbackDetails = (open: boolean) => {
    setCashbackDetailsVisible(open);
  };
    console.log(ShoppingCartV2," ShoppingCartV2 ");

  return (
    <div
      id="ShoppingCart"
      className={
        'shopping_cart ' +
        className +
        (agentInfo?.agentMode ? ' shopping_cart-agent' : '') +
        (shoppingCartOpen || isMobileView ? ' cart-open' : '')
      }
    >
      {generateCssVariables('#ShoppingCart', config?.theme)}

      {showCloseButton && (
        <AiFillCloseCircle
          size={30}
          className="close"
          onClick={() => {
            closeFromHeader && closeFromHeader();
            setShoppingCartOpen(false);
          }}
          id="btn-close-cart"
        />
      )}
      <ShoppingCartHeader
        checkout={isCheckoutPage}
        setShoppingCartOpen={setShoppingCartOpen}
        shoppingCartOpen={shoppingCartOpen || Boolean(open)}
        setAddUserModal={setAddUserModal}
        diamondsCount={  ShoppingCartV2?.total_diamonds ?? ShoppingCartV2?.diamonds}
      />
      <AddUserModal
        addUserModal={addUserModal}
        setAddUserModal={setAddUserModal}
      />

      {(shoppingCartOpen || Boolean(open)) &&
        (ShoppingCartV2?.cartNotValid ? (
          <CartNotValid />
        ) : (
          <>
            {isLocalInventory ? 
            <div className='localInventoryContainer'>
              <h3>{t?.localInventoryAlert}</h3>
            </div>
              : !ShoppingCartV2?.orderId ? (
              <ShoppingCartEmpty />
            ) : (
              <div className="content">
                {agentInfo.agentMode && (
                  <AgentActions
                    addUserModal={addUserModal}
                    setAddUserModal={setAddUserModal}
                    isMangeOrderPage={isMangeOrderPage}
                  />
                )}
                {ShoppingCartV2?.products.map((product) => {
                  return (
                    <React.Fragment key={product.identifier}>
                      {ProductRenderer({
                        product,
                        identifier: product.identifier,
                        isTopLevel: true,
                        topLevelProductId: product.identifier || product.id,
                      })}
                    </React.Fragment>
                  );
                })}

                {appliedSubsidize?.length > 0 && <ShoppingCartSubsidize />}
              </div>
            )}

            {settings?.show_coupon_code && showDiscounCodeOrIntegrationInfo && (
              <DiscountCode
                t={t}
                shoppingCart={ShoppingCartV2 as any}
                pageType={pageType}
              />
            )}
            {
              <div className="footer">
                <Tooltip
                  title={
                    !ShoppingCartV2?.orderId ? t.shoppingCartButtonTooltip : ''
                  }
                >
                  <div
                    className="btn-style-5"
                    onClick={!isCheckoutPage ? handleCheckout : undefined}
                  >
                    {!freePackage ? (
                      <>
                        <ul className={'total-cart-pricing'}>
                          {/* {config?.siteConfig?.shareB2C?.cashback?.is_active && cashbackDetailsVisible && (
                            <CashbackDetails
                              close={() => showCashbackDetails(false)}
                              cashbackCount={ShoppingCartV2?.diamonds}
                              t_wallet={t_wallet}
                            />
                          )} */}
                          {/* {
                            config?.siteConfig?.shareB2C?.cashback?.is_active && (
                              <li className={'total-agent-comm'}>
                                <div className="label">{"Total diamonds 💎"}</div>
                                <div
                                  style={{
                                    display: 'flex',
                                    gap: 10,
                                    alignItems: 'center',
                                  }}
                                >
                                  {ShoppingCartV2?.diamonds || 0} ILS
                                </div>
                              </li>
                            )
                          } */}
                          {agentInfo?.agentMode &&
                            !!ShoppingCartV2?.orderId && (
                              <li className={'total-agent-comm'}>
                                <div className="label">{t.totalAgentComm}</div>
                                {/* {config?.siteConfig?.shareB2C?.cashback?.is_active && (
                                  <div
                                    className="wallet-icon"
                                    onClick={(e) => {
                                      e?.stopPropagation();
                                      e?.preventDefault();
                                      showCashbackDetails(true);
                                    }}
                                  >
                                    <DiamondSVG width={24} height={24} />
                                  </div>
                                )} */}
                                <div
                                  style={{
                                    display: 'flex',
                                    gap: 10,
                                    alignItems: 'center',
                                  }}
                                >
                                  {isUpdateCommissionNeeded() &&
                                    ShoppingCartV2?.orderSecret &&
                                    !ShoppingCartV2?.isSaved && (
                                      // <div className="update-commission">
                                      //   <Tooltip title={t.commissionNotSaved}>
                                      //     <ButtonWithIcon
                                      //       onClick={handleUpdateCommission}
                                      //       loading={isUpdateCommissionLoading}
                                      //       // icon={<MdWarning color="yellow" />}
                                      //       icon={<FaRegSave />}
                                      //       text={t.updateCommission}
                                      //       className="btn-style-1"
                                      //     ></ButtonWithIcon>
                                      //   </Tooltip>
                                      // </div>

                                      <div className="update-commission">
                                        <Tooltip title={t.commissionNotSaved}>
                                          <Button
                                            onClick={handleUpdateCommission}
                                            loading={isUpdateCommissionLoading}
                                            className="btn-style-1"
                                          >
                                            {t.updateCommission}
                                          </Button>
                                        </Tooltip>
                                      </div>
                                    )}
                                  {/* {(
                                Number(ShoppingCartV2?.total_price) -
                                ShoppingCartV2?.total_net_price
                                ).toFixed(2)} */}
                                  {(
                                    Number(ShoppingCartV2?.total_price) -
                                    ShoppingCartV2?.total_net_price
                                  ).toFixed(2)}
                                  {String.fromCharCode(160)}
                                  {ShoppingCartV2?.currency_sign}
                                </div>
                              </li>
                            )}
                            {
                              (agentInfo?.agentMode && config?.siteConfig?.shareB2C?.cashback?.is_active) &&
                              (ShoppingCartV2?.redeemedDiamonds?.length ?? 0) > 0 && (
                                <li className={'total-agent-comm redeemed-list'}>
                                  <div className="redeemed-rows">
                                    {/* Subtotal (previous price before discounts) */}
                                    <div className="redeemed-row subtotal-row">
                                      <div className="row-label">{t_wallet.Subtotal}</div>
                                      <div className="row-amount subtotal-amount">
                                        {(Number(ShoppingCartV2?.total_price_before_discount)).toFixed(2)} {String.fromCharCode(160)}
                                            {ShoppingCartV2?.currency_sign}
                                      </div>
                                    </div>

                                    {/* Each redeemed discount (one row per redeemedDiamonds item) */}
                                    {ShoppingCartV2?.redeemedDiamonds?.map((amt: number | string, i: number) => {
                                      const signed = Number(amt) || 0; // probably negative
                                      // Show as "- 50 $" (space after minus for the visual style)
                                      const display = signed < 0 ? `- ${Math.abs(signed)}` : `- ${signed}`;
                                      return (
                                        <div className="redeemed-row discount-row" key={`redeemed-${i}`}>
                                          <div className="row-label">{t_wallet.discount} {t_wallet.diamonds}</div>
                                          <div className="row-amount discount-amount">
                                            {display} {String.fromCharCode(160)}
                                            ILS
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </li>
                              )
                            }


                          <li>
                            <div>
                              {t.totalPrice}
                              {String.fromCharCode(160)}
                              <span className="currency-sign">
                                {ShoppingCartV2?.currency_sign}
                              </span>
                            </div>
                            <div>
                              {ShoppingCartV2?.total_price?.toFixed(0)}
                              {String.fromCharCode(160)}
                              {ShoppingCartV2?.currency_sign}
                            </div>
                          </li>
                        </ul>
                      </>
                    ) : (
                      <div className="free-package">{t.placeOrder}</div>
                    )}
                  </div>
                </Tooltip>
                {!!ShoppingCartV2?.orderId &&
                !isCheckoutPage &&
                !freePackage ? (
                  <div
                    className="move-to-checkout"
                    onClick={!isCheckoutPage ? handleCheckout : undefined}
                  >
                    {t.proceedToPayment}
                  </div>
                ) : null}
              </div>
            }
          </>
        ))}
    </div>
  );
};

export default ShoppingCartV2;
